import { ComponentProps, FunctionComponent } from "react";
import AutoSizer from "react-virtualized-auto-sizer";

import { Alert } from "antd";
import styled from "styled-components/macro";

import ConfirmDialog from "components/shared/ConfirmDialog";

import BusyIndicator from "./BusyIndicator";
import { AlertContainer, StyledGrid, paginationHeight } from "./DataTable.styles";
import Pagination from "./Pagination";

type GridProps = ComponentProps<typeof StyledGrid>;

interface DataTableBodyProps {
  className: string;
  columns: GridProps["columns"];
  rows: GridProps["rows"];
  sortColumns: GridProps["sortColumns"];
  selectedRows: GridProps["selectedRows"];
  toolbarHeight: number;
  pagination: {
    show: boolean;
    pageSize: number;
    maxPageSize: number;
    totalResults: number;
  };
  awaitingLargeLoadConfirm: boolean;
  errorMessage: Nullable<string>;
  isFetching: boolean;
  onSortColumnsChange: GridProps["onSortColumnsChange"];
  onSelectedRowsChange: GridProps["onSelectedRowsChange"];
  onRowClick: GridProps["onRowClick"];
  onConfirmLargeLoad: () => void;
}

// The Data view's grid, footer and the overlays over them. The monthly view renders its
// own, so none of this runs there.
const DataTableBody: FunctionComponent<DataTableBodyProps> = ({
  className,
  columns,
  rows,
  sortColumns,
  selectedRows,
  toolbarHeight,
  pagination,
  awaitingLargeLoadConfirm,
  errorMessage,
  isFetching,
  onSortColumnsChange,
  onSelectedRowsChange,
  onRowClick,
  onConfirmLargeLoad
}) => (
  <>
    <AutoSizer>
      {({ width, height }) => (
        <StyledGrid
          className={className}
          columns={columns}
          rows={rows}
          rowKeyGetter={(row) => row.uwi}
          sortColumns={sortColumns}
          selectedRows={selectedRows}
          style={{
            width: `${Number(width)}px`,
            height: pagination.show
              ? `${Number(height - toolbarHeight - paginationHeight)}px`
              : `${Number(height - toolbarHeight)}px`
          }}
          onSortColumnsChange={onSortColumnsChange}
          onSelectedRowsChange={onSelectedRowsChange}
          onRowClick={onRowClick}
        />
      )}
    </AutoSizer>

    {pagination.show && (
      <Pagination
        pageSize={pagination.pageSize}
        maxPageSize={pagination.maxPageSize}
        totalResults={pagination.totalResults}
      />
    )}

    <ConfirmIndicator className={`${!awaitingLargeLoadConfirm ? "hidden" : ""}`}>
      <ConfirmDialog
        title="Large amount of data"
        message="You are attempting to load a large amount of data which will be loaded across multiple pages."
        onConfirm={onConfirmLargeLoad}
      />
    </ConfirmIndicator>

    {errorMessage && (
      <AlertContainer>
        <Alert type="error" message={errorMessage}></Alert>
      </AlertContainer>
    )}

    <BusyIndicator visible={isFetching} />
  </>
);

export default DataTableBody;

const ConfirmIndicator = styled.div`
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(211, 211, 211, 0.424);
  z-index: 5;

  &.hidden {
    display: none;
  }

  .title {
    font-size: 2.2rem;
    font-weight: var(--fontWeightBold);
    color: var(--color-text);
  }

  .content {
    p {
      font-size: 1.4rem;
      font-weight: var(--fontWeightMedium);
      margin-bottom: 15px;
    }

    .button {
      height: 38px;
      width: 100px;
      border-radius: 4px;

      span {
        font-size: 1.6rem;
      }
    }

    .actions {
      gap: 10px;
    }
  }
`;
